import Phaser from 'phaser';
import { THEME } from '@/config/theme';
import type { Grade } from '@/config/scoring';
import { BACKGROUNDS } from '@/config/settings';
import { AUDIO_KEYS, getActiveAudioPack, getAudioFileName } from '@/config/audioPacks';
import { MORSE_SPACING } from '@/utils/timing';
import { PerformanceJudgeTracker } from '@/systems/performanceJudge';
import { fetchDailyShare } from '@/services/dailyShare';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Create loading bar
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(THEME.colors.backgroundLightHex, 0.8);
    progressBox.fillRect(width / 2 - 160, height / 2 - 25, 320, 50);

    const loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
    });
    loadingText.setOrigin(0.5, 0.5);

    const percentText = this.add.text(width / 2, height / 2, '0%', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    });
    percentText.setOrigin(0.5, 0.5);

    // Loading progress events
    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(THEME.colors.primaryHex, 1);
      progressBar.fillRect(width / 2 - 150, height / 2 - 15, 300 * value, 30);
      percentText.setText(`${Math.round(value * 100)}%`);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
      percentText.destroy();
    });

    // Load assets
    for (const bg of BACKGROUNDS) {
      this.load.image(`bg-${bg.id}`, bg.file);
    }
    this.load.image('samuel-morse', '/assets/images/samuel-fb-morse.jpg');
    const audioPack = getActiveAudioPack();
    for (const key of AUDIO_KEYS) {
      const fileName = getAudioFileName(key);
      this.load.audio(key, `${audioPack.basePath}/${fileName}`);
    }
  }

  create(): void {
    // Generate sprites programmatically
    this.createSprites();

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const rawSceneParam = params.get('scene');
      const sceneParam = rawSceneParam ? rawSceneParam.toLowerCase() : null;
      if (sceneParam === 'result') {
        this.scene.start('ResultScene', this.buildMockResultData());
        return;
      }

      if (sceneParam === 'daily-result') {
        this.scene.start('DailyResultScene', this.buildMockDailyResultData());
        return;
      }

      const dailyParam = params.get('daily');
      if (dailyParam !== null) {
        const requestedDate = dailyParam.trim() || null;
        this.scene.start('DailyChallengeScene', { requestedDate });
        return;
      }

      const path = window.location.pathname || '';
      const shareMatch = path.match(/^\/s\/([^/]+)$/);
      if (shareMatch) {
        const shareId = shareMatch[1];
        if (shareId) {
          void this.loadDailyShare(shareId.trim());
          return;
        }
      }

      // Deep link for online co-op joins: /j/<code>
      const joinMatch = path.match(/^\/j\/([^/]+)$/);
      if (joinMatch) {
        const roomId = joinMatch[1]?.trim();
        if (roomId) {
          window.history.replaceState(null, '', '/');
          this.scene.start('JoinScene', { roomId });
          return;
        }
      }
    }

    this.scene.start('MenuScene');
  }

  private async loadDailyShare(shareId: string): Promise<void> {
    const result = await fetchDailyShare(shareId);
    if (result.payload) {
      this.scene.start('DailyShareScene', { payload: result.payload });
      return;
    }
    const message = result.error ? `Share unavailable (${result.error})` : 'Share not found.';
    this.scene.start('DailyShareScene', { payload: null, error: message });
  }

  private buildMockResultData(): {
    score: number;
    streak: number;
    decodedMessage: string;
    intendedMessage: string;
    accuracy: number;
    grade: 'A';
    levelIndex: number;
    difficulty: string;
    judge: {
      timingDeltas: Array<{
        valuePx: number;
        phase: 'keydown' | 'keyup' | 'combined';
        symbol: '.' | '-';
        letter: string;
      }>;
      factors: Array<{
        id: string;
        label: string;
        value: number;
        multiplier: number;
        score: number;
        advice: string;
      }>;
      selected: {
        id: string;
        label: string;
        value: number;
        multiplier: number;
        score: number;
        advice: string;
      } | null;
    };
  } {
    const tracker = new PerformanceJudgeTracker();
    const mockOffsets: Array<{ keydown: number; keyup: number; symbol: '.' | '-'; letter: string }> = [
      { keydown: -8, keyup: -6, symbol: '.', letter: 'S' },
      { keydown: -7, keyup: -5, symbol: '.', letter: 'S' },
      { keydown: -9, keyup: -4, symbol: '.', letter: 'S' },
      { keydown: 12, keyup: 16, symbol: '-', letter: 'O' },
      { keydown: 10, keyup: 14, symbol: '-', letter: 'O' },
      { keydown: 13, keyup: 18, symbol: '-', letter: 'O' },
      { keydown: -6, keyup: -3, symbol: '.', letter: 'S' },
      { keydown: -5, keyup: -4, symbol: '.', letter: 'S' },
      { keydown: -7, keyup: -2, symbol: '.', letter: 'S' },
    ];

    for (const entry of mockOffsets) {
      tracker.recordKeydown({
        offsetPx: entry.keydown,
        symbol: entry.symbol,
        letter: entry.letter,
      });
      tracker.recordKeyup({
        offsetPx: entry.keyup,
        symbol: entry.symbol,
        letter: entry.letter,
      });
    }

    const judge = tracker.getResults();

    return {
      score: 1558,
      streak: 9,
      decodedMessage: 'SOS',
      intendedMessage: 'SOS',
      accuracy: 94.95,
      grade: 'A',
      levelIndex: 0,
      difficulty: 'medium',
      judge: {
        timingDeltas: judge.timingDeltas,
        factors: judge.factors,
        selected: judge.selected,
      },
    };
  }

  private buildMockDailyResultData(): {
    score: number;
    streak: number;
    decodedMessage: string;
    intendedMessage: string;
    accuracy: number;
    grade: Grade;
    dailyDate: string;
    dailyId: string;
    eliminated: boolean;
  } {
    return {
      score: 276,
      streak: 4,
      decodedMessage: 'Parrot flock descended on a clay bank and the birds clung to the vertical surface while eating mineral rich soil Hundreds gathered at the site each morning and their bright colors stood out against the pale exposed earth',
      intendedMessage: 'S',
      accuracy: 92.73,
      grade: 'B',
      dailyDate: '2025-12-23',
      dailyId: 'mock-daily-123',
      eliminated: false,
    };
  }

  private createSprites(): void {
    // Dot sprite
    const dotGraphics = this.make.graphics({ x: 0, y: 0 });
    dotGraphics.fillStyle(THEME.colors.primaryHex, 1);
    dotGraphics.fillRect(0, 0, MORSE_SPACING.dotWidth, MORSE_SPACING.noteHeight);
    dotGraphics.generateTexture('dot-sprite', MORSE_SPACING.dotWidth, MORSE_SPACING.noteHeight);
    dotGraphics.destroy();

    // Dash sprite
    const dashGraphics = this.make.graphics({ x: 0, y: 0 });
    dashGraphics.fillStyle(THEME.colors.primaryHex, 1);
    dashGraphics.fillRect(0, 0, MORSE_SPACING.dashWidth, MORSE_SPACING.noteHeight);
    dashGraphics.generateTexture('dash-sprite', MORSE_SPACING.dashWidth, MORSE_SPACING.noteHeight);
    dashGraphics.destroy();
  }
}
